import { LoadingButton } from '@mui/lab';
import { Button, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Attachment, AttachmentInput } from 'sr-types/lib/production/v1/graphql';
import type { Reference } from 'sr-types/lib/search/v1/graphql';
import { getFullName } from '../../person/person';
import type { EntityName } from '../auth/Security';
import { SECURITY_ACTIONS, useEntitySecurityPath, useSecurity } from '../auth/Security';
import { UserContext } from '../auth/UserContext';
import Confirm from '../components/Confirm';
import Loading from '../components/Loading';
import { useTrackDeepLinkItemId } from '../deepLink';
import I18n from '../i18n/I18n';
import { H, V } from '../Layout';
import { failedToastMessage, successToastMessage } from '../utils/commonUtils';
import { acceptedFilesMock } from './attachmentsApi';
import AttachmentsEditor from './AttachmentsEditor';
import AttachmentShareSettings from './AttachmentShareSettings';
import AttachmentsView from './AttachmentsView';
import {
    applyOptimisticAttachments,
    applySavedAttachmentIds,
    buildOptimisticSaveAttachments,
    createAttachmentInput,
    defaultMaxFileSize,
    getAttachmentDeletionFailureReason,
    getAttachmentSaveFailureReason,
    mergeAttachmentsByReference,
    mergeSavedAttachments,
    normalizeTags,
    removeAttachmentByReferenceId
} from './attachmentUtils';
import { useDeleteAttachments } from './data/useDeleteAttachments';
import { useGetAttachments } from './data/useGetAttachments';
import { useSaveAttachment } from './data/useSaveAttachment';
import type { AttachmentLinkDialogTriggerProps } from './types';
import { useAttachmentsSharePanel } from './useAttachmentsSharePanel';

type AttachmentsDisplayProps = AttachmentLinkDialogTriggerProps & {
    id: string;
    /** Overrides the built-in save; used by surfaces that keep attachments in form state until the entity is saved. */
    onSave?: (entityId: string, input: AttachmentInput[], contextTerm: string) => Promise<any>;
    entityReference?: Reference;
    maxFileSize?: number;
    acceptedFiles?: Array<string>;
    filesLimit?: number;
    attachmentsInState?: Array<AttachmentInput>;
    onDeleteAttachmentFromState?: (attachments: AttachmentInput[], input: AttachmentInput) => AttachmentInput[];
    disableDropzone?: boolean;
    hideDropzone?: boolean;
    setIsInUploadMode?: Dispatch<SetStateAction<boolean>>;
    contextTerm?: string;
    hideAction?: boolean;
    entityOriginId?: string;
    entity: EntityName;
    defaultMandatoryTags?: Array<string>;
    enableLinkAttachment?: boolean;
    leftAlignContent?: boolean;
    disableButtons?: boolean;
    autoOpenFilePickerTrigger?: number;
    /** Called after an auto-open file picker request is consumed. */
    onAutoOpenFilePickerHandled?: () => void;
    enableSelection?: boolean;
    disabledSelectedAttachmentIds?: ReadonlySet<string>;
    onAddSelectedAttachments?: (attachments: Attachment[]) => void;
    onCancelSelection?: () => void;
    /** Called after saveAttachments returns ids for newly saved files or links. */
    onSaveAttachments?: (attachments: Attachment[]) => void;
};

const EMPTY_ATTACHMENTS: AttachmentInput[] = [];

export default function AttachmentsDisplay(props: AttachmentsDisplayProps) {
    const {
        entityReference,
        onSave,
        maxFileSize = defaultMaxFileSize,
        acceptedFiles = acceptedFilesMock,
        filesLimit = 10,
        attachmentsInState = EMPTY_ATTACHMENTS,
        onDeleteAttachmentFromState = undefined,
        disableDropzone = false,
        hideDropzone = false,
        setIsInUploadMode = () => {},
        contextTerm = '',
        hideAction = false,
        entityOriginId = '',
        entity,
        defaultMandatoryTags = [],
        enableLinkAttachment = true,
        leftAlignContent = false,
        disableButtons = false,
        autoOpenFilePickerTrigger,
        onAutoOpenFilePickerHandled,
        autoOpenLinkDialogTrigger,
        onAutoOpenLinkDialogHandled,
        enableSelection = false,
        disabledSelectedAttachmentIds,
        onAddSelectedAttachments,
        onCancelSelection,
        onSaveAttachments
    } = props;

    const { userProfile } = useContext(UserContext);
    const { allowed: hasEntityCommentPermission } = useSecurity(SECURITY_ACTIONS.COMMENT, {
        entityName: entity
    });
    const attachmentEntityId = entityOriginId || entityReference?.id;
    const skipAttachmentsQuery = !attachmentEntityId;
    const attachmentsResponse = useGetAttachments(
        entity,
        attachmentEntityId,
        skipAttachmentsQuery,
        contextTerm
    );
    const deepLinkUIStatePath = useEntitySecurityPath();
    const { deleteAttachment } = useDeleteAttachments();
    const { attachmentSave } = useSaveAttachment();

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [fileToDelete, setFileToDelete] = useState<AttachmentInput>();
    const [filesToEdit, setFilesToEdit] = useState<AttachmentInput[]>([]);
    const [localAttachments, setLocalAttachments] = useState<AttachmentInput[]>([...attachmentsInState]);
    const [showAttachmentsViewState, setShowAttachmentsViewState] = useState(false);
    const [sharePanelOpen, setSharePanelOpen] = useState(false);
    const [openShareForAttachmentId, setOpenShareForAttachmentId] = useState<string | null>(null);
    const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<Set<string>>(() => new Set());
    const disabledSelectedIds = useMemo<ReadonlySet<string>>(
        () => disabledSelectedAttachmentIds ?? new Set<string>(),
        [disabledSelectedAttachmentIds]
    );

    useTrackDeepLinkItemId(openShareForAttachmentId, deepLinkUIStatePath);

    const { closeSharePanel, openShareForExistingAttachment } = useAttachmentsSharePanel({
        sharePanelOpen,
        setSharePanelOpen,
        openShareForAttachmentId,
        setOpenShareForAttachmentId
    });

    useEffect(() => {
        if (!attachmentsResponse?.loading && attachmentsResponse?.data) {
            const apiAttachments = attachmentsResponse?.data?.attachments || [];
            const mergedAttachments = mergeAttachmentsByReference([...apiAttachments, ...attachmentsInState]);
            const nextSignature = mergedAttachments
                .map((attachment: AttachmentInput) => JSON.stringify(attachment))
                .join('|');

            setLocalAttachments((previousAttachments) => {
                const previousSignature = previousAttachments
                    .map((attachment: AttachmentInput) => JSON.stringify(attachment))
                    .join('|');
                return previousSignature === nextSignature ? previousAttachments : mergedAttachments;
            });

            setShowAttachmentsViewState((previousState) => {
                const nextState = mergedAttachments.length > 0;
                return previousState === nextState ? previousState : nextState;
            });
        }
    }, [attachmentsInState, attachmentsResponse?.data, attachmentsResponse?.loading]);

    const onDeleteAttachment = useCallback(
        async (input: AttachmentInput) => {
            if (!input) return;

            setIsDeleteModalOpen(false);

            const referenceId = input.entityReference?.id;
            if (
                onDeleteAttachmentFromState &&
                attachmentsInState?.some((attachment) => attachment.entityReference?.id === referenceId)
            ) {
                const filteredAttachments = onDeleteAttachmentFromState(localAttachments, input);
                if (filteredAttachments) {
                    setLocalAttachments(filteredAttachments);
                }
                return;
            }

            const previousAttachments = localAttachments;
            const nextAttachments = removeAttachmentByReferenceId(previousAttachments, referenceId);
            const rollback = applyOptimisticAttachments(setLocalAttachments, previousAttachments, nextAttachments);

            try {
                await deleteAttachment(entity, entityReference?.id, createAttachmentInput(input));
                successToastMessage('attachment.delete.success');
                if (onDeleteAttachmentFromState) {
                    const syncedAttachments = onDeleteAttachmentFromState(nextAttachments, input);
                    if (syncedAttachments) {
                        setLocalAttachments(syncedAttachments);
                    }
                }
            } catch (error) {
                rollback();
                const reason = getAttachmentDeletionFailureReason(error);
                if (reason) {
                    failedToastMessage('attachment.delete.error.reason', { message: reason });
                } else {
                    failedToastMessage('attachment.delete.error');
                }
            }
        },
        [attachmentsInState, deleteAttachment, entity, entityReference, localAttachments, onDeleteAttachmentFromState]
    );

    const onEditAttachment = useCallback((file: Attachment) => {
        const { tags, ...rest } = file;
        setFilesToEdit((prev) => {
            const isFileAlreadyBeingEdited = prev.some((item) => item.entityReference?.id === file.entityReference?.id);
            if (!isFileAlreadyBeingEdited) {
                return [...prev, { ...rest, tagSet: tags }];
            }
            return prev;
        });
    }, []);

    const handleCancelEdit = (file: AttachmentInput) => {
        if (!file.entityReference?.id) return;
        setFilesToEdit((prev) => prev.filter((item) => item.entityReference?.id !== file.entityReference?.id));

        setLocalAttachments((prev) => {
            if (!prev.some((attachment) => attachment.entityReference?.id === file.entityReference?.id)) {
                return [...prev, { ...file, tags: normalizeTags(file) }];
            }
            return prev;
        });
    };

    const handleSave = useCallback(
        async (entityId: string, input: AttachmentInput[]) => {
            if (!input.length) return;

            const attachmentsToSave = input.map((file) => ({
                ...file,
                createdOn: new Date().toISOString(),
                createdBy: userProfile?.accountId,
                createdByName: getFullName(userProfile?.name),
                tags: normalizeTags(file)
            }));
            const previousAttachments = localAttachments;
            const rollback = applyOptimisticAttachments(
                setLocalAttachments,
                previousAttachments,
                buildOptimisticSaveAttachments(previousAttachments, attachmentsToSave)
            );
            setShowAttachmentsViewState(true);

            try {
                const res = onSave
                    ? await onSave(entityId, input, contextTerm)
                    : await attachmentSave(entity, entityId, input, contextTerm);

                if (!res || res?.data?.errors) {
                    rollback();
                    failedToastMessage('attachment.save.error');
                    return;
                }

                if (!onSave) {
                    const hasMissingId = res.data.saveAttachments?.some((attachment) => !attachment.id);
                    if (hasMissingId) {
                        failedToastMessage('attachment.save.error');
                    } else {
                        successToastMessage('attachment.save.success');
                    }
                }

                const savedAttachments = applySavedAttachmentIds(attachmentsToSave, res);
                setLocalAttachments((prev) => mergeSavedAttachments(prev, savedAttachments));
                setFilesToEdit((prev) =>
                    prev.filter((file) => !input.some((item) => item.entityReference?.id === file.entityReference?.id))
                );
                return res;
            } catch (error) {
                rollback();
                const reason = getAttachmentSaveFailureReason(error);
                if (reason) {
                    failedToastMessage('attachment.save.error.reason', { message: reason });
                } else {
                    failedToastMessage('attachment.save.error');
                }
            }
        },
        [attachmentSave, contextTerm, entity, localAttachments, onSave, userProfile?.accountId, userProfile?.name]
    );

    const handleOpenDeleteModal = useCallback((file: Attachment) => {
        setFileToDelete({ ...file, ...createAttachmentInput(file) });
        setIsDeleteModalOpen(true);
    }, []);

    const handleOpenSharePanel = useCallback(
        (file: Attachment) => {
            openShareForExistingAttachment(file?.entityReference?.id ?? null);
        },
        [openShareForExistingAttachment]
    );

    const selectedAttachment =
        localAttachments.find((attachment) => attachment.entityReference?.id === openShareForAttachmentId) ?? null;

    const isUserSelectedAttachment = useCallback(
        (attachmentId?: string) =>
            Boolean(attachmentId && selectedAttachmentIds.has(attachmentId) && !disabledSelectedIds.has(attachmentId)),
        [disabledSelectedIds, selectedAttachmentIds]
    );

    const toggleSelectedAttachment = useCallback(
        (attachmentId: string) => {
            if (!enableSelection) {
                return;
            }
            if (disabledSelectedIds.has(attachmentId)) {
                return;
            }

            setSelectedAttachmentIds((prev) => {
                const next = new Set(prev);
                if (next.has(attachmentId)) {
                    next.delete(attachmentId);
                } else {
                    next.add(attachmentId);
                }
                return next;
            });
        },
        [disabledSelectedIds, enableSelection]
    );

    const userSelectedAttachments = useMemo<Attachment[]>(
        () =>
            localAttachments
                .filter((attachment) => {
                    const attachmentId = (attachment as Attachment).id;
                    return isUserSelectedAttachment(attachmentId);
                })
                .map(
                    (attachment) =>
                        ({
                            ...attachment,
                            tags: normalizeTags(attachment)
                        }) as Attachment
                ),
        [isUserSelectedAttachment, localAttachments]
    );

    const handleCancelSelection = useCallback(() => {
        setSelectedAttachmentIds(new Set());
        onCancelSelection?.();
    }, [onCancelSelection]);

    const handleAddSelectedAttachments = useCallback(() => {
        onAddSelectedAttachments?.(userSelectedAttachments);
        setSelectedAttachmentIds(new Set());
    }, [onAddSelectedAttachments, userSelectedAttachments]);

    useEffect(() => {
        if (!enableSelection) {
            setSelectedAttachmentIds(new Set());
        }
    }, [enableSelection]);

    useEffect(() => {
        if (sharePanelOpen && openShareForAttachmentId && !selectedAttachment) {
            closeSharePanel();
        }
    }, [closeSharePanel, openShareForAttachmentId, selectedAttachment, sharePanelOpen]);

    const shouldHideDropzone = hideDropzone || (localAttachments && localAttachments.length > 0);

    const attachmentsContent = (
        <>
            {hasEntityCommentPermission && (
                <AttachmentsEditor
                    entityReference={entityReference}
                    maxFileSize={maxFileSize}
                    acceptedFiles={acceptedFiles}
                    filesLimit={filesLimit}
                    insideSidePanel={false}
                    onSave={handleSave}
                    disableDropzone={disableDropzone}
                    filesToEdit={filesToEdit}
                    setFilesToEdit={setFilesToEdit}
                    onCancelEdit={handleCancelEdit}
                    hideDropzone={shouldHideDropzone}
                    setIsInUploadMode={setIsInUploadMode}
                    localAttachments={localAttachments}
                    setLocalAttachments={setLocalAttachments}
                    setShowAttachmentsViewState={setShowAttachmentsViewState}
                    defaultMandatoryTags={defaultMandatoryTags}
                    enableLinkAttachment={enableLinkAttachment}
                    leftAlignContent={leftAlignContent}
                    disableButtons={disableButtons}
                    autoOpenFilePickerTrigger={autoOpenFilePickerTrigger}
                    onAutoOpenFilePickerHandled={onAutoOpenFilePickerHandled}
                    autoOpenLinkDialogTrigger={autoOpenLinkDialogTrigger}
                    onAutoOpenLinkDialogHandled={onAutoOpenLinkDialogHandled}
                    onSaveAttachments={onSaveAttachments}
                />
            )}

            {showAttachmentsViewState && (
                <AttachmentsView
                    attachments={localAttachments}
                    deepLinkUIStatePath={deepLinkUIStatePath}
                    onDeleteAttachment={handleOpenDeleteModal}
                    onEditAttachment={onEditAttachment}
                    onShareAttachment={handleOpenSharePanel}
                    hideAction={hideAction}
                    hasEntityCommentPermission={hasEntityCommentPermission}
                    enableSelection={enableSelection}
                    selectedAttachmentIds={selectedAttachmentIds}
                    disabledSelectedAttachmentIds={disabledSelectedIds}
                    onToggleAttachmentSelection={toggleSelectedAttachment}
                />
            )}
        </>
    );

    return (
        <>
            <V fill sx={{ minHeight: 0, overflow: 'hidden', alignItems: 'stretch' }}>
                {attachmentsResponse?.loading ? (
                    <V grow sx={{ alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                        <Loading />
                    </V>
                ) : (
                    <V
                        gap={1}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            p: 1,
                            pl: 0,
                            overflow: 'auto'
                        }}
                    >
                        {sharePanelOpen ? (
                            <Box sx={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}>
                                <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>{attachmentsContent}</Box>
                                <Box sx={{ width: '100%' }}>
                                    <AttachmentShareSettings
                                        attachmentId={openShareForAttachmentId}
                                        attachment={selectedAttachment as Attachment | null}
                                        onClose={closeSharePanel}
                                        onAfterShareSettingsSave={attachmentsResponse?.refetch}
                                    />
                                </Box>
                            </Box>
                        ) : (
                            attachmentsContent
                        )}
                    </V>
                )}
                {enableSelection ? (
                    <H
                        sx={{
                            flexShrink: 0,
                            width: '100%',
                            py: 1.5,
                            px: 1,
                            borderTop: '1px solid',
                            borderTopColor: 'divider',
                            bgcolor: 'background.paper',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1,
                            zIndex: 1
                        }}
                    >
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleCancelSelection}
                            sx={{ textTransform: 'none' }}
                        >
                            <I18n token="dialog.cancel" />
                        </Button>
                        <LoadingButton
                            variant="contained"
                            size="small"
                            onClick={handleAddSelectedAttachments}
                            disabled={!onAddSelectedAttachments || userSelectedAttachments.length === 0}
                            sx={{ textTransform: 'none' }}
                        >
                            <I18n token="common.add" />
                        </LoadingButton>
                    </H>
                ) : null}
            </V>
            <Confirm
                open={isDeleteModalOpen}
                title={<I18n token="delete.confirm.title" />}
                okLabelI18n="delete.confirm.title"
                body={
                    <Typography>
                        <I18n token="delete.confirm.message" />
                    </Typography>
                }
                onConfirm={() => onDeleteAttachment(fileToDelete)}
                onCancel={() => setIsDeleteModalOpen(false)}
            />
        </>
    );
}
